// src/pages/Search.tsx
// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import RecipeCardWide from '../components/Shared/RecipeCards/RecipeCardWide.tsx';
import SearchBarWithChips from '../components/SearchBarWithChips.tsx';
import AddToMealPlanSheet from '../components/MealPlanner/AddToMealPlanSheet.tsx';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner.tsx';
import fetchWithAuth from '../utils/fetchWithAuth.ts';
import { getCurrentWeekStart } from '../utils/getCurrentWeekStart.ts';
import { getAvatarURL } from '../utils/avatar.ts';
import { PiBowlFoodFill, PiUsersThreeFill } from 'react-icons/pi';
import TagSelector from '../components/ui/molecules/TagSelector.tsx';
import FollowButton from '../components/ui/atoms/FollowButton.tsx';
import WarningBottomSheet, {
  WarningType,
} from '../components/ui/atoms/Warning/WarningBottomSheet.tsx';
import type { Recipe } from '../types/recipe';
import { computeWarning } from '../utils/computeWarning';
import type { Prefs } from '../types/preferences';

type User = {
  id: string;
  name?: string;
  username?: string;
  bio?: string;
  favoriteTags?: string[];
};
type TagMap = Record<string, { id: string; name: string }[]>;

const MAX_RECIPES_PER_PAGE = 10;
const MAX_USERS_PER_PAGE = 10;

interface SearchProps {
  onlyRecipes?: boolean;
  onRecipeAdd?: (recipe: Recipe) => void;
}

const Search: React.FC<SearchProps> = ({
  onlyRecipes = false,
  onRecipeAdd,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    'DIET'
  );
  const [showFilters, setShowFilters] = useState(false);
  const [mode, setMode] = useState<'recipes' | 'people'>('recipes');

  // ─── Recipe‐search state ─────────────────────────────────────────────────
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [tags, setTags] = useState<TagMap>({});
  const [loadingRecipes, setLoadingRecipes] = useState(true);
  const [isFetchingRecipes, setIsFetchingRecipes] = useState(false);
  const [recipePage, setRecipePage] = useState(1);
  const [hasMoreRecipes, setHasMoreRecipes] = useState(true);
  const [recipeScrollEnabled, setRecipeScrollEnabled] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQueries = searchParams.getAll('query');
  const initialTags = searchParams.getAll('tags');
  const [activeTerms, setActiveTerms] = useState<string[]>(initialQueries);
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);

  // ─── People‐search state ─────────────────────────────────────────────────
  const [users, setUsers] = useState<User[]>([]);
  const [searchTermUsers, setSearchTermUsers] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [userScrollEnabled, setUserScrollEnabled] = useState(false);

  // ─── Meal‐plan sheet ──────────────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRecipe, setPickerRecipe] = useState<Recipe | null>(null);

  // ─── Warning sheet data & user prefs ────────────────────────────────────
  const [warningData, setWarningData] = useState<{
    type: WarningType;
    imageUrl?: string;
    title?: string;
    reasons: string[];
  } | null>(null);

  const [prefs, setPrefs] = useState<Prefs>({
    avoided: [],
    allergic: [],
    excludedTagIds: [],
  });

  useEffect(() => {
    fetchWithAuth('/api/user/preferences')
      .then((up) => {
        setPrefs({
          avoided: (up.avoidedIngredients || []).map((i: any) =>
            typeof i === 'string' ? i : i.name
          ),
          allergic: (up.allergicIngredients || []).map((i: any) =>
            typeof i === 'string' ? i : i.name
          ),
          excludedTagIds: up.excludedTagIds || [],
        });
      })
      .catch(console.error);
  }, []);

  // ─── Fetch tags once ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchWithAuth(`/api/tags`)
      .then((data) => {
        if (data.manualTags && data.autoTags) {
          const combinedTags: TagMap = {};
          Object.entries(data.autoTags).forEach(([cat, tgs]) => {
            combinedTags[cat] = [...(combinedTags[cat] || []), ...tgs];
          });
          Object.entries(data.manualTags).forEach(([cat, tgs]) => {
            const existing = combinedTags[cat]?.map((t) => t.id) || [];
            const unique = tgs.filter((t: any) => !existing.includes(t.id));
            combinedTags[cat] = [...(combinedTags[cat] || []), ...unique];
          });
          setTags(combinedTags);
        }
      })
      .catch(console.error);
  }, []);

  // ─── Fetch recipes (page/filters) ────────────────────────────────────────
  const fetchRecipes = async (page = 1) => {
    if (page > 1 && (isFetchingRecipes || !hasMoreRecipes)) return;
    setIsFetchingRecipes(true);

    const params = new URLSearchParams();
    activeTerms.forEach((t) => params.append('query', t));
    selectedTags.forEach((t) => params.append('tags', t));
    params.append('page', String(page));
    params.append('limit', String(MAX_RECIPES_PER_PAGE));

    try {
      const { recipes: newRecipes } = await fetchWithAuth(
        `/api/recipes/search?${params.toString()}`
      );
      setRecipes((prev) =>
        page === 1 ? newRecipes : [...prev, ...newRecipes]
      );
      setHasMoreRecipes(newRecipes.length === MAX_RECIPES_PER_PAGE);
      if (page === 1) setRecipeScrollEnabled(true);
    } catch (e) {
      console.error('Error fetching recipes:', e);
      setHasMoreRecipes(false);
    } finally {
      setIsFetchingRecipes(false);
      setLoadingRecipes(false);
    }
  };

  // ─── Effects: params <> URL <> fetch ────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams();
    activeTerms.forEach((t) => params.append('query', t));
    selectedTags.forEach((t) => params.append('tags', t));
    setSearchParams(params, { replace: true });

    if (mode === 'recipes') {
      setLoadingRecipes(true);
      setRecipePage(1);
      setHasMoreRecipes(true);
      setRecipeScrollEnabled(false);
      fetchRecipes(1);
    }
  }, [activeTerms, selectedTags, mode]);

  useEffect(() => {
    if (mode !== 'recipes' || !recipeScrollEnabled || !hasMoreRecipes) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingRecipes) {
          const next = recipePage + 1;
          setRecipePage(next);
          fetchRecipes(next);
        }
      },
      { threshold: 1 }
    );
    const trg = document.getElementById('recipe-scroll-trigger');
    if (trg) obs.observe(trg);
    return () => obs.disconnect();
  }, [
    mode,
    recipeScrollEnabled,
    hasMoreRecipes,
    isFetchingRecipes,
    recipePage,
  ]);

  // ─── Fetch users ─────────────────────────────────────────────────────────
  const fetchUsers = async (page = 1) => {
    if (page > 1 && (isFetchingUsers || !hasMoreUsers)) return;
    setIsFetchingUsers(true);
    try {
      const { users: newUsers, hasMore: more } = await fetchWithAuth(
        `/api/users?page=${page}&limit=${MAX_USERS_PER_PAGE}`
      );
      setUsers((prev) => (page === 1 ? newUsers : [...prev, ...newUsers]));
      setHasMoreUsers(more);
      if (page === 1) setUserScrollEnabled(true);
    } catch (e) {
      console.error('Error fetching users:', e);
      setHasMoreUsers(false);
    } finally {
      setLoadingUsers(false);
      setIsFetchingUsers(false);
    }
  };

  useEffect(() => {
    if (mode !== 'people') return;
    setLoadingUsers(true);
    setUserPage(1);
    setHasMoreUsers(true);
    setUserScrollEnabled(false);
    fetchUsers(1);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'people' || !userScrollEnabled || !hasMoreUsers) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingUsers) {
          const next = userPage + 1;
          setUserPage(next);
          fetchUsers(next);
        }
      },
      { threshold: 1 }
    );
    const trg = document.getElementById('people-scroll-trigger');
    if (trg) obs.observe(trg);
    return () => obs.disconnect();
  }, [mode, userScrollEnabled, hasMoreUsers, isFetchingUsers, userPage]);

  const filteredUsers = users.filter((u) => {
    const n = (u.name || '').toLowerCase();
    const un = (u.username || '').toLowerCase();
    return n.includes(searchTermUsers) || un.includes(searchTermUsers);
  });

  const openSheet = (r: Recipe) => {
    setPickerRecipe(r);
    setPickerOpen(true);
  };

  return (
    <div className="px-4 pb-20 pt- max-w-5xl mx-auto">
      {/* Header & Tabs */}
      {/* <div className="sticky top-0 bg-white z-20"> */}
      <div
        class="sticky bg-white z-20"
        style={{ top: 'var(--safe-top)' }}
      >
        {!onlyRecipes && (
          <div className="flex border-b mb-4">
            <button
              onClick={() => setMode('recipes')}
              className={`flex-1 py-2 flex items-center justify-center gap-2 border-b-2 ${
                mode === 'recipes'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <PiBowlFoodFill className="w-5 h-5" /> Recipes
            </button>
            <button
              onClick={() => setMode('people')}
              className={`flex-1 py-2 flex items-center justify-center gap-2 border-b-2 ${
                mode === 'people'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <PiUsersThreeFill className="w-5 h-5" /> People
            </button>
          </div>
        )}
        {mode === 'recipes' ? (
          <>
            <SearchBarWithChips
              onIngredientsChange={setActiveTerms}
              selectedTags={selectedTags}
              onTagToggle={(id) =>
                setSelectedTags((p) =>
                  p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
                )
              }
              tags={tags}
              onCategoryToggle={setSelectedCategory}
              selectedCategory={selectedCategory}
              showFilters={showFilters}
              toggleFilters={() => setShowFilters((s) => !s)}
              setSelectedTags={setSelectedTags}
              chips={activeTerms}
              setChips={setActiveTerms}
            />
            {showFilters && (
              <TagSelector
                tags={tags}
                selectedTags={selectedTags}
                onTagToggle={(id) =>
                  setSelectedTags((p) =>
                    p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
                  )
                }
                selectedCategory={selectedCategory}
                showFilters={showFilters}
                toggleFilters={() => setShowFilters((s) => !s)}
                setSelectedCategory={setSelectedCategory}
              />
            )}
          </>
        ) : (
          !onlyRecipes && (
            <input
              type="text"
              placeholder="Search users..."
              className="w-full px-4 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary mb-2"
              value={searchTermUsers}
              onChange={(e) => setSearchTermUsers(e.target.value.toLowerCase())}
            />
          )
        )}
      </div>

      {/* Recipes */}
      {mode === 'recipes' && (
        <>
          {loadingRecipes || (isFetchingRecipes && recipes.length === 0) ? (
            <div className="text-center mt-10 text-gray-500">
              <BrandSpinner />
            </div>
          ) : recipes.length === 0 ? (
            <p className="text-gray-600">No recipes found.</p>
          ) : (
            <>
              <div className="space-y-2">
                {recipes.map((r) => {
                  const warning = computeWarning(r, prefs);
                  return (
                    <RecipeCardWide
                      key={r.id}
                      recipe={r}
                      isBookmarked={r.isBookmarked}
                      onToggleBookmark={async () => {
                        try {
                          if (r.isBookmarked) {
                            await fetchWithAuth(`/api/bookmarks`, {
                              method: 'DELETE',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ recipeId: r.id }),
                            });
                          } else {
                            await fetchWithAuth(`/api/bookmarks`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ recipeId: r.id }),
                            });
                          }
                          setRecipes((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, isBookmarked: !x.isBookmarked }
                                : x
                            )
                          );
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      onAddToPlan={() =>
                        onRecipeAdd ? onRecipeAdd(r) : openSheet(r)
                      }
                      hasWarning={Boolean(warning)}
                      warningType={warning?.type}
                      onWarningClick={() => {
                        if (!warning) return;
                        setWarningData({
                          type: warning.type,
                          imageUrl: r.imageUrl,
                          title: r.title,
                          reasons: warning.reasons,
                        });
                      }}
                    />
                  );
                })}
              </div>
              <div id="recipe-scroll-trigger" className="h-1" />
              {isFetchingRecipes && (
                <div className="text-center mt-4">
                  <BrandSpinner />
                </div>
              )}
            </>
          )}

          {pickerRecipe && (
            <AddToMealPlanSheet
              isOpen={pickerOpen}
              onClose={() => setPickerOpen(false)}
              recipe={{
                id: pickerRecipe.id,
                title: pickerRecipe.title,
                imageUrl: pickerRecipe.imageUrl,
              }}
              weekStart={getCurrentWeekStart()}
            />
          )}

          {warningData && (
            <WarningBottomSheet
              isOpen
              onClose={() => setWarningData(null)}
              type={warningData.type}
              imageUrl={warningData.imageUrl}
              title={warningData.title}
              reasons={warningData.reasons}
            />
          )}
        </>
      )}

      {/* People */}
      {!onlyRecipes && mode === 'people' && (
        <>
          {loadingUsers || (isFetchingUsers && users.length === 0) ? (
            <div className="text-center mt-10 text-gray-500">
              <BrandSpinner />
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-gray-600">No users found.</p>
          ) : (
            <>
              <div className="space-y-3 mt-3">
                {filteredUsers.map((u) => (
                  <Link
                    key={u.id}
                    to={`/app/profile/${u.id}`}
                    className="block relative hover:bg-gray-50 transition rounded-lg"
                  >
                    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-300">
                      <img
                        src={getAvatarURL(u)}
                        alt={u.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {u.name || `@${u.username}`}
                        </h3>
                        <p className="text-sm text-gray-500 mb-1">
                          @{u.username}
                        </p>
                        {u.favoriteTags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {u.favoriteTags.map((tag, i) => (
                              <span
                                key={i}
                                className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <FollowButton
                        userId={u.id} 
                        isFollowing={u.isFollowing}
                        mode="icon" 
                      />
                    </div>
                  </Link>
                ))}
              </div>
              <div id="people-scroll-trigger" className="h-1" />
              {isFetchingUsers && (
                <div className="text-center mt-4">
                  <BrandSpinner />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Search;
